// The firm's challenge templates: the organisation authors, edits, publishes
// and archives its own here, through the Firm API, with the ONE builder the
// trader app also hosts (@noctant/challenge-builder). A platform preset is
// copied, never edited.

import {
  moneyText,
  requestToBuilder,
  sponsorshipLine,
  templateRequest,
  type CreateTemplateRequest,
  type CustomChallengeBuilderInput,
  type TemplateSummary,
} from "@noctant/challenge-builder";
import "@noctant/challenge-builder/builder/builder.css";
import { ChallengeBuilder } from "@noctant/challenge-builder/ui";
import { For, Match, Show, Switch, createResource, createSignal } from "solid-js";

import AsyncResource from "../components/AsyncResource";
import CursorPager from "../components/CursorPager";
import { needsFirm } from "../components/FirmPicker";
import PageHeader from "../components/PageHeader";
import { api, failureSentence, type TemplateListCursor } from "../lib/api";
import { createCursorPages } from "../lib/cursorPages";
import { loadRuleText } from "../lib/ruleText";
import { canMutate, useSession } from "../lib/session";
import "./templates.css";
import { firmWords } from "../lib/orgWords";

const PAGE_SIZE = 50;

/** What the builder is open on: a new template, or a copy or one of ours to
 *  edit, with the request it was loaded from (every term the form does not
 *  show is sent back from it). */
type Editing =
  | { kind: "new" }
  | { kind: "copy" | "edit"; templateId: string; form: CustomChallengeBuilderInput; base: CreateTemplateRequest };


function revisionText(t: TemplateSummary): string {
  if (t.revision_no == null) return "Draft";
  return `Revision ${t.revision_no} · ${t.visibility ?? "private"}`;
}

export default function Templates() {
  const session = useSession();
  const words = () => firmWords(session.admin());
  const mayChange = () => {
    const admin = session.admin();
    return admin ? canMutate(admin.role) : false;
  };
  const pages = createCursorPages<TemplateListCursor>();
  const [listed, { refetch }] = createResource(
    () => !needsFirm(session.admin()?.role ?? "") && { cursor: pages.current() },
    ({ cursor }) => api.listTemplates({ limit: PAGE_SIZE, ...cursor }),
  );
  const [editing, setEditing] = createSignal<Editing | null>(null);
  // The builder's live preview reads challenge-text: loaded the first time a
  // builder opens.
  const [ruleText] = createResource(() => editing() !== null || undefined, loadRuleText);
  const [saving, setSaving] = createSignal(false);
  const [message, setMessage] = createSignal<string | null>(null);

  async function open(t: TemplateSummary, purpose: "copy" | "edit") {
    setMessage(null);
    try {
      const prefill = requestToBuilder(await api.getTemplateAuthoring(t.template_id), purpose);
      if (prefill.kind === "refused") {
        setMessage(prefill.reason);
        return;
      }
      setEditing({ kind: purpose, templateId: t.template_id, form: prefill.form, base: prefill.base });
    } catch (e) {
      setMessage(failureSentence(e));
    }
  }

  async function save(form: CustomChallengeBuilderInput) {
    const open = editing();
    if (!open) return;
    setSaving(true);
    setMessage(null);
    try {
      const request = templateRequest(form, open.kind === "new" ? undefined : open.base);
      const { template } = open.kind === "edit"
        ? await api.updateTemplate(open.templateId, request)
        : await api.createTemplate(request);
      setEditing(null);
      void refetch();
      // Saving is the firm's call; the attach's own rule is said with it.
      setMessage(`Saved ${template.display_name}. Publish it to offer it. ${sponsorshipLine(form)}`);
    } catch (e) {
      setMessage(failureSentence(e));
    } finally {
      setSaving(false);
    }
  }

  async function publish(t: TemplateSummary) {
    setMessage(null);
    try {
      const published = await api.publishTemplate(t.template_id, { visibility: "org" });
      void refetch();
      setMessage(`Published ${t.display_name} as revision ${published.revision_no}.`);
    } catch (e) {
      setMessage(failureSentence(e));
    }
  }

  async function archive(t: TemplateSummary) {
    if (!confirm(`Archive ${t.display_name}? ${words()?.area ?? ""} already running keep their rules.`)) return;
    setMessage(null);
    try {
      await api.archiveTemplate(t.template_id);
      void refetch();
      setMessage(`Archived ${t.display_name}.`);
    } catch (e) {
      setMessage(failureSentence(e));
    }
  }

  return (
    <>
      <PageHeader
        title="Templates"
        subtitle={`The ${words()?.runs ?? ""} your firm offers`}
        actions={
          <Show when={mayChange() && editing() === null}>
            <button class="btn-primary" type="button" onClick={() => setEditing({ kind: "new" })}>
              New template
            </button>
          </Show>
        }
      />
      <Show when={message()}>
        {(text) => <p class="text-sm text-slate-700 mb-4" role="status">{text()}</p>}
      </Show>

      <Switch>
        <Match when={editing()} keyed>
          {(open) => {
            const prefilled = open.kind === "new" ? null : open;
            const title = { new: "New template", copy: "Copy and edit", edit: "Edit template" }[open.kind];
            return (
              <div class="firm-builder">
                <div class="flex items-center justify-between mb-3">
                  <h2 class="text-base font-semibold">{title}</h2>
                  <button class="btn-secondary" type="button" onClick={() => setEditing(null)}>
                    Back to templates
                  </button>
                </div>
                <AsyncResource resource={ruleText}>
                  {() => (
                    <Show when={words()}>
                    {(said) => (
                    <ChallengeBuilder
                      governance={said().governance}
                      authority="firm"
                      sponsorship
                      disabled={saving()}
                      initial={prefilled?.form}
                      confirm={(question) => Promise.resolve(confirm(question.message))}
                      onSubmit={(form) => void save(form)}
                    />
                    )}
                    </Show>
                  )}
                </AsyncResource>
              </div>
            );
          }}
        </Match>
        <Match when={editing() === null}>
          <div class="card">
            <AsyncResource resource={listed}>
              {(page) => (
                <>
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>Template</th>
                        <th>Size</th>
                        <th>Published</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      <For each={page().templates}>
                        {(t) => (
                          <tr>
                            <td>
                              <div class="font-medium">{t.display_name}</div>
                              <div class="text-xs text-slate-500">{t.can_edit ? "Your firm's" : "Platform preset"}</div>
                            </td>
                            {/* The size is money in the template's own currency, once the words have loaded. */}
                            <td><Show when={words()}>{moneyText(t.account_size, t.currency, "cents_when_present")}</Show></td>
                            <td>{revisionText(t)}</td>
                            <td class="text-right whitespace-nowrap space-x-2">
                              <Show when={mayChange()}>
                                <Show when={t.can_edit}>
                                  <button class="btn-secondary" type="button" onClick={() => void open(t, "edit")}>Edit</button>
                                  <button class="btn-secondary" type="button" onClick={() => void publish(t)}>Publish</button>
                                  <button class="btn-danger" type="button" onClick={() => void archive(t)}>Archive</button>
                                </Show>
                                <button class="btn-secondary" type="button" onClick={() => void open(t, "copy")}>Copy</button>
                              </Show>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                  <div class="card-body">
                    <CursorPager pages={pages} next={page().next_cursor} />
                  </div>
                </>
              )}
            </AsyncResource>
          </div>
        </Match>
      </Switch>
    </>
  );
}
